/**
 * Lightweight custom tooltip system.
 *
 * Usage: set `data-tooltip` on any element. A single shared tooltip node is
 * reused for all triggers — positioned above the trigger on hover/focus, hides
 * on blur/leave/escape. Works for mouse, keyboard, and touch (tap to show,
 * tap elsewhere to hide).
 *
 * CSS lives in profile.css under `.tooltip`.
 */

let tip: HTMLElement | null = null;
let installed = false;
let activeTrigger: HTMLElement | null = null;

function ensureTip(): HTMLElement {
  if (tip) return tip;
  tip = document.createElement("div");
  tip.className = "tooltip";
  tip.setAttribute("role", "tooltip");
  tip.setAttribute("aria-hidden", "true");
  document.body.append(tip);
  return tip;
}

function show(trigger: HTMLElement) {
  const text = trigger.getAttribute("data-tooltip");
  if (!text) return;
  const t = ensureTip();
  t.textContent = text;
  t.setAttribute("aria-hidden", "false");
  t.classList.add("tooltip--visible");
  activeTrigger = trigger;
  position(trigger, t);
}

function hide() {
  if (!tip) return;
  tip.classList.remove("tooltip--visible");
  tip.setAttribute("aria-hidden", "true");
  activeTrigger = null;
}

function position(trigger: HTMLElement, t: HTMLElement) {
  const r = trigger.getBoundingClientRect();
  // Measure after content change so width is correct.
  const tr = t.getBoundingClientRect();
  const gap = 8;
  let x = r.left + r.width / 2 - tr.width / 2;
  let y = r.top - tr.height - gap;
  // Flip below if it would clip above the viewport.
  if (y < 4) y = r.bottom + gap;
  x = Math.max(4, Math.min(window.innerWidth - tr.width - 4, x));
  t.style.transform = `translate(${x}px, ${y}px)`;
}

function onEnter(ev: Event) {
  const el = (ev.target as HTMLElement | null)?.closest?.("[data-tooltip]");
  if (el instanceof HTMLElement) show(el);
}

function onLeave(ev: Event) {
  const el = (ev.target as HTMLElement | null)?.closest?.("[data-tooltip]");
  if (el && el === activeTrigger) hide();
}

function onDocClick(ev: Event) {
  const el = (ev.target as HTMLElement | null)?.closest?.("[data-tooltip]");
  if (el instanceof HTMLElement) {
    if (activeTrigger === el) {
      hide();
    } else {
      show(el);
    }
  } else {
    hide();
  }
}

function onKey(ev: KeyboardEvent) {
  if (ev.key === "Escape") hide();
}

function onScrollOrResize() {
  if (activeTrigger && tip) position(activeTrigger, tip);
}

/**
 * Install global tooltip listeners. Safe to call multiple times.
 */
export function installTooltips() {
  if (installed) return;
  installed = true;
  document.addEventListener("pointerenter", onEnter, true);
  document.addEventListener("pointerleave", onLeave, true);
  document.addEventListener("focusin", onEnter);
  document.addEventListener("focusout", onLeave);
  document.addEventListener("click", onDocClick);
  document.addEventListener("keydown", onKey);
  window.addEventListener("scroll", onScrollOrResize, { passive: true });
  window.addEventListener("resize", onScrollOrResize);
}
